/** Shared raw-input façade for browser runtime and offline parity gates. */
import { assertUncomputedFromBase, fromBaseRecord, withFromBaseValidation, withFromBaseReadSharing } from './from-base-context';
import type { FromBaseRow, FromBasePlayerInput } from './from-base-context';
import { compilePlayerFromBase } from './from-base-player-entry';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import { compileInventoryTreeFromBase } from './from-base-inventory';
import type { FromBaseInventoryTree, FromBaseInventoryMetadata } from './from-base-inventory';
import { compileEncounterFromBase, compileEncounterFromBaseAsync, encounterInventoryInputFromBase, compileEncounterCatalogFromBase } from './from-base-encounter';
import { createEncounterMemberMemo } from './encounter-member-memo';
import { encounterMemberInput } from './encounter-member-input';
import { memberConditionContext } from './member-condition-context';
import { compileEncounterShellFromBase } from './from-base-encounter-shell';
import type { FromBaseEncounter } from './from-base-encounter';
import { compileLocationFromBase } from './from-base-location';
import type { FromBaseLocation, FromBaseLocationMetadata } from './from-base-location';
import { compileDefinitionFinalizationEntryFromBase } from './from-base-definition-finalization';
import { compileNpcFinalizationEntryFromBase, createNpcDefinitionFinalizer } from './from-base-npc-finalization';
import { readInventoryField } from './inventory-raw-readers';
import type { FromBaseDefinitionCache } from './from-base-definition-finalization';
import { mergeInventoryMetadata } from './inventory-shared-catalog';
import { fromBaseStageIds as stageIds } from './from-base-stage-ids';
import { foldPassiveIntegersFromBase } from './from-base-passive-integer-fold';
import { ftClockCampaignField } from '../../shared/campaign-clock';
import { withAuraOwnerState, type AuraOwnerStateResolver } from './creature-status-reader';
import { encounterCreatureField } from '../../shared/array-types';

export class NoFromBaseCompiler extends Error {}
export interface FromBaseEntryMetadata {
    /** Held owner truth, supplied by the browser/worker, outside the raw envelope. */
    auraOwner?: AuraOwnerStateResolver;
    player: FromBasePlayerResourceMetadata;
    inventory: FromBaseInventoryMetadata;
    encounter: NonNullable<FromBaseEncounter['encounter_metadata']>;
    location: FromBaseLocationMetadata;
    /** SELECT-only supplemental inputs, never an oracle or a prior compiled host. */
    inputs?: Record<string, unknown>;
}
export interface FromBaseEntryResult {
    computed: unknown; stagesRun: string[]; stagesMissing: string[]; stageDiagnostics?: readonly string[];
    /** Native inventory first-load donors, retained for publication with births. */
    definition_catalog?: Readonly<Record<string, FromBaseRow>>;
    /** R-209 ownership, independent of the effective factor; only present after capacity coverage. */
    capacityOwned?: boolean;
}

export function compileEntityFromBase(entity: string, base: unknown, metadata: FromBaseEntryMetadata,
    cache: FromBaseDefinitionCache = { prepared: new Map(), served: new Set() },
    capacityRefusal: 'capacity-multiplier-passive-fold' | 'member-capacity-browser-fold' = 'capacity-multiplier-passive-fold',
    retainInventoryDefinitions = false): FromBaseEntryResult {
    return withAuraOwnerState(metadata.auraOwner, () => withFromBaseReadSharing(() => withFromBaseValidation(() => compileValidatedEntityFromBase(entity, base, metadata, cache, capacityRefusal, retainInventoryDefinitions))));
}

/** Creature pages have saved encounter headers but no scene. Member inputs are
 * explicitly encounter-scoped before arriving here; losing their roster must
 * never turn a range condition off. Existing full scene inputs remain strict. */
function creatureConditionInput(base: FromBaseRow, inputs: FromBaseEntryMetadata['inputs']): FromBaseRow {
    const input = { ...base, ...inputs }, context = input.encounter_context;
    if (!fromBaseRecord(context) || !('summon_range_scope' in context)
        && !('encounter_creatures_arr' in context) && !('controlled_summons' in context))
        input.encounter_context = { ...fromBaseRecord(context) ? context : {}, summon_range_scope: 'sheet' };
    return input;
}

function compileValidatedEntityFromBase(entity: string, base: unknown, metadata: FromBaseEntryMetadata,
    cache: FromBaseDefinitionCache, capacityRefusal: 'capacity-multiplier-passive-fold' | 'member-capacity-browser-fold', retainInventoryDefinitions: boolean): FromBaseEntryResult {
    // A saved floor mount rides the page's own catalog entries (compiled templates) beside its raw
    // rows; they seed the lean stage below and are not raw input.
    const heldCatalog = entity === 'encounter-floor' && fromBaseRecord(base) && fromBaseRecord(base.held_catalog) ? base.held_catalog : undefined;
    if (heldCatalog) base = Object.fromEntries(Object.entries(base as FromBaseRow).filter(([key]) => key !== 'held_catalog'));
    assertUncomputedFromBase(base);
    assertUncomputedFromBase(metadata, 'metadata');
    const fold = (result: FromBaseEntryResult, input: FromBaseRow, inventory: FromBaseInventoryMetadata) => {
        let finalizer: ReturnType<typeof createNpcDefinitionFinalizer> | undefined;
        // Read through the completed compile's cache without inserting, replacing
        // or marking entries served. A fold must not change later members' reads.
        const readCache: FromBaseDefinitionCache = {
            prepared: new class extends Map<string, FromBaseRow> {
                override get(key: string) { return cache.prepared.get(key); }
                override set(_key: string, _row: FromBaseRow): this { return this; }
            }(),
            served: new class extends Set<string> {
                override has(key: string) { return cache.served.has(key); }
                override add(_key: string): this { return this; }
            }(),
        };
        return foldPassiveIntegersFromBase(result, id => {
            finalizer ??= createNpcDefinitionFinalizer(input, metadata.player, inventory, readCache);
            return finalizer.definition(id, []);
        }, capacityRefusal, input.missing_definition_ids as readonly string[] | undefined,
            input.encounter_context && fromBaseRecord(input.encounter_context) && fromBaseRecord(input.encounter_context.campaign_arr)
                ? input.encounter_context.campaign_arr : null);
    };
    if (entity === 'pc' || entity === 'creature' && fromBaseRecord(base) && fromBaseRecord(base.player_characters)) {
        const input = creatureConditionInput(base as FromBaseRow, metadata.inputs) as unknown as FromBasePlayerInput;
        const result = compilePlayerFromBase(input, metadata.player, cache);
        const finalized = { ...result, stagesRun: stageIds(result.stagesRun), stagesMissing: stageIds(result.stagesMissing) };
        if (finalized.stagesMissing.length) return finalized;
        return fold(finalized,
            input as unknown as FromBaseRow, mergeInventoryMetadata(metadata.player.inventoryMetadata ?? metadata.inventory, input.inventory_metadata));
    }
    if (entity === 'definition' || entity === 'creature') {
        const input = entity === 'creature' ? creatureConditionInput(base as FromBaseRow, metadata.inputs)
            : { ...base as FromBaseRow, ...metadata.inputs };
        const inventory = mergeInventoryMetadata(metadata.inventory, fromBaseRecord(input.inventory_metadata) ? input.inventory_metadata : undefined);
        const result = entity === 'definition'
            ? compileDefinitionFinalizationEntryFromBase(input, inventory,
                createNpcDefinitionFinalizer(input, metadata.player, inventory, cache))
            : compileNpcFinalizationEntryFromBase(input, metadata.player, inventory, cache);
        const finalized = { computed: result.row, stagesRun: stageIds(result.completedStages),
            stagesMissing: stageIds(result.missingStages), stageDiagnostics: result.diagnostics };
        return entity === 'definition' ? finalized : fold(finalized, input, inventory);
    }
    if (entity === 'inventory') {
        const input = { ...base as FromBaseInventoryTree, ...metadata.inputs, metadata: metadata.inventory };
        const result = compileInventoryTreeFromBase(input, (base as FromBaseRow).catalog as Record<string, FromBaseRow>);
        return { computed: result.inventory, stagesRun: stageIds(result.completedStages), stagesMissing: stageIds(result.missingStages),
            ...(retainInventoryDefinitions ? { definition_catalog: result.definitions } : {}),
            stageDiagnostics: result.detail ? [...result.missingStages, result.detail] : result.missingStages };
    }
    if (entity === 'encounter') {
        const input = prepareEncounterFromBase(base, metadata, cache);
        if (metadata.encounter.projection === 'shell') {
            const result = compileEncounterShellFromBase(input, (base as FromBaseRow).catalog as Record<string, FromBaseRow>, input.encounter_metadata);
            return { computed: result.row, stagesRun: stageIds(result.completedStages), stagesMissing: stageIds(result.missingStages), stageDiagnostics: result.missingStages };
        }
        return encounterResult(compileEncounterFromBase(input, (base as FromBaseRow).catalog as Record<string, FromBaseRow>));
    }
    // A saved floor mount uses the encounter's own inputs and lean projection,
    // without running any encounter shell, terrain or member producer.
    if (entity === 'encounter-floor') {
        const input = prepareEncounterFromBase(base, metadata, cache);
        const definitions = (base as FromBaseRow).catalog as Record<string, FromBaseRow>;
        const result = compileInventoryTreeFromBase(encounterInventoryInputFromBase(input, input.encounter_metadata!), definitions);
        const computed = result.inventory && !result.missingStages.length
            ? compileEncounterCatalogFromBase({ encounters: {}, computed: {}, encounter_inventories_arr: result.inventory },
                definitions, input.encounter_metadata, input.random_table_rows, heldCatalog) : undefined;
        return { computed, stagesRun: stageIds(result.completedStages), stagesMissing: stageIds(result.missingStages),
            stageDiagnostics: result.detail ? [...result.missingStages, result.detail] : result.missingStages };
    }
    if (entity === 'location') {
        const result = compileLocationFromBase({ ...base as FromBaseLocation, ...metadata.inputs }, metadata.location);
        // The native location builder attaches its hierarchy after the surface stage.
        return { computed: result.row === undefined ? undefined : { ...result.row, ...result.hierarchyProjection?.row }, stagesRun: stageIds(result.completedStages), stagesMissing: stageIds(result.missingStages), stageDiagnostics: result.missingStages };
    }
    throw new NoFromBaseCompiler(`No from-base compiler for ${entity}`);
}

function prepareEncounterFromBase(base: unknown, metadata: FromBaseEntryMetadata, cache: FromBaseDefinitionCache,
    members?: ReturnType<typeof createEncounterMemberMemo>, dependencyKey?: string): FromBaseEncounter {
    const input = { ...base as FromBaseEncounter, ...metadata.inputs, encounter_metadata: metadata.encounter };
    const sharedOrder = input.inventory_order;
    const orderFor = (rows: unknown) => {
        const ids = new Set((Array.isArray(rows) ? rows : []).map(row => readInventoryField(row, 'inventory_id')));
        return sharedOrder?.filter(row => ids.has(readInventoryField({ inventory: row }, 'inventory_id')));
    };
    const inventory = mergeInventoryMetadata(metadata.inventory, input.inventory_metadata);
    let finalizer: ReturnType<typeof createNpcDefinitionFinalizer> | undefined;
    const definitionLookup = (id: string) => {
        if (!metadata.player) return null;
        finalizer ??= createNpcDefinitionFinalizer(input as unknown as FromBaseRow, metadata.player, inventory, cache);
        return finalizer.definition(id, []);
    };
    // An inline, unversioned closure has no reusable definition witness.
    // A patch keeps completed members whose saved inputs and authored epoch did not change.
    const memberBase = { ...input };
    const memo = dependencyKey ? members?.encounter(input.entity_id,
        input.encounter_creatures_arr.map(row => String(row.entity_id)), dependencyKey, cache, true) : undefined;
    const completedOwners = new Map<string, FromBaseEntryResult>();
    const constructing = new Set<string>();
    const owner = (id: string) => {
        const held = metadata.auraOwner?.(id);
        if (held) return held;
        const source = input.encounter_creatures_arr.find(row => String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase() === id.toUpperCase());
        if (!source) return null;
        // This is the initial member construction, once per owner, never a delta
        // or a payload-read recompile. Completed members are reused by the roster.
        const result = compileMember(source);
        return fromBaseRecord(result.computed) ? result.computed : null;
    };
    const compileMember = (member: FromBaseRow): FromBaseEntryResult => {
        const id = String(member.entity_id).toUpperCase();
        const done = completedOwners.get(id);
        if (done) return done;
        if (constructing.has(id)) throw new Error('unsupported-creature_payload_statuses:aura-owner-cycle');
        constructing.add(id);
        try {
            const resolve = (source: FromBaseRow) => {
                const result = compileMember(source);
                if (!fromBaseRecord(result.computed) || result.stagesMissing.length)
                    throw new Error('summon-within:geometry-not-held');
                return result.computed;
            };
            const context = memberConditionContext(memberBase as unknown as FromBaseRow, member, resolve);
            const saved = { ...member, encounter_context: context };
            const compile = (memberCache = cache) => compileEntityFromBase('creature',
                encounterMemberInput(memberBase as unknown as FromBaseRow, member, resolve),
                { ...metadata, auraOwner: owner, inputs: undefined }, memberCache, 'member-capacity-browser-fold');
            const result = memo ? memo(saved, compile) : compile();
            completedOwners.set(id, result);
            return result;
        } finally { constructing.delete(id); }
    };
    input.encounter_metadata = { ...metadata.encounter, inventory: metadata.inventory ? inventory : undefined,
        ...(metadata.player ? { definitionLookup, memberCompiler: compileMember } : {}) };
    input.inventory_order = orderFor(input.inventory);
    return input;
}

const encounterResult = (result: ReturnType<typeof compileEncounterFromBase>): FromBaseEntryResult => ({
    computed: result.row, stagesRun: stageIds(result.completedStages), stagesMissing: stageIds(result.missingStages),
    stageDiagnostics: [...result.missingStages, ...result.diagnostics ?? []],
});

/** Worker-owned compiler; synchronous callers retain the identical offline entry. */
export function createEntityCompiler(immutableInputs = false) {
    const members = createEncounterMemberMemo(immutableInputs);
    const compile = async (entity: string, base: unknown, metadata: FromBaseEntryMetadata, catalogVersion?: string,
        current: () => boolean = () => true, progress: () => void = () => {}): Promise<FromBaseEntryResult> => {
        if (entity !== 'encounter' || metadata.encounter.projection === 'shell') return compileEntityFromBase(entity, base, metadata);
        const seen = new WeakSet<object>(), cache: FromBaseDefinitionCache = { prepared: new Map(), served: new Set() };
        const input = withFromBaseValidation(() => {
            assertUncomputedFromBase(base); assertUncomputedFromBase(metadata, 'metadata');
            const row = base as FromBaseRow;
            // The current campaign context is part of each member's saved witness.
            // Its revision also advances for clock-only writes, which need no member compile.
            const campaignId = String(ftClockCampaignField(row.campaign_arr, 'campaign_id') ?? '').toUpperCase();
            const memberIds = new Set((row.encounter_creatures_arr as FromBaseRow[]).map(member => String(member.entity_id).toUpperCase()));
            for (const member of row.encounter_creatures_arr as FromBaseRow[])
                for (const node of Array.isArray(member.inventory) ? member.inventory : []) memberIds.add(String(readInventoryField(node, 'inventory_id')).toUpperCase());
            for (const node of Array.isArray(row.inventory) ? row.inventory : []) memberIds.add(String(readInventoryField(node, 'inventory_id')).toUpperCase());
            const revisions = fromBaseRecord(row.dependency_revs) ? Object.entries(row.dependency_revs)
                .filter(([id]) => !memberIds.has(id.toUpperCase()) && id.toUpperCase() !== String(row.entity_id).toUpperCase()
                    && id.toUpperCase() !== campaignId).sort() : [];
            const foreign = fromBaseRecord(row.dependency_revs_invalidate_only) ? Object.entries(row.dependency_revs_invalidate_only)
                .filter(([id]) => id.toUpperCase() !== campaignId).sort() : [];
            return prepareEncounterFromBase(base, metadata, cache, members,
                catalogVersion ? JSON.stringify([catalogVersion, revisions, foreign]) : undefined);
        }, seen);
        return encounterResult(await compileEncounterFromBaseAsync(input, (base as FromBaseRow).catalog as Record<string, FromBaseRow>, seen, current, progress));
    };
    return { compile, clear: members.clear, stats: members.stats, memberCompiles: members.memberCompiles };
}
